/**
 * IGNIERE filter panel — 開閉/排他/popstate/SP scroll lock（Phase C 版）
 * spec: 70_デザイン/作業/filter-shopify_2026-07-20/spec.md Phase C
 *
 * 責務：
 *   - PC/SP 開閉トグル（PC push-out / SP bottom-sheet）
 *   - __menuX 排他 + 実オーバーレイ bridge（theme-drawer:open で filter close）
 *   - SP 時のみ dialog 意味論付与＋body scroll lock＋inert フォーカストラップ（厳密化）
 *   - ESC / backdrop / fp-close / fp-show で閉じる（user close のみ Filter ボタンへ focus 復帰）
 *   - open 時は closeボタン or 最初の入力へ focus 移動（PC は自動 focus なし）
 *   - popstate 補完（無条件 reload・Codex R5 対応済）
 *   - pagehide で body scroll 解除（BFCache 対策）
 *   - init() は idempotent（AbortController で listener cleanup・多重初期化防止）
 *
 * facets.js（Horizon 純正）の form 送信・URL 生成・件数計算・morph には介入しない。
 * DOM 契約 8 件は igniere-filter-panel.liquid 側で維持。
 */
(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────
  // __menuX 排他コーディネーター（他 overlay と共有・グローバル）
  // idempotent 初期化
  // ─────────────────────────────────────────────────────────────
  if (!window.__menuX) {
    window.__menuX = {
      // Phase H-5 v5 H2：register は unregister 関数を返す（teardown で解除可能に）
      register: function (id, closeFn) {
        var handler = function (e) { if (e.detail !== id) closeFn(); };
        document.addEventListener('menu:open', handler);
        return function unregister() { document.removeEventListener('menu:open', handler); };
      },
      announce: function (id) {
        document.dispatchEvent(new CustomEvent('menu:open', { detail: id }));
      },
    };
  }

  var MQ = '(min-width: 990px)'; // PC = 990+ / SP = 989-
  var F_ID = 'filter';

  // C-9 + Phase H-5 v5 H2：多重初期化防止 + 完全 teardown
  var _abortCtrl = null;
  var _menuXUnregister = null;
  var _focusTimer = null;
  var _prevBodyOverflow = null;
  var _currentInerted = [];
  var _morphRestoreFrame = null;
  var _morphTransitionResumeFrame = null;
  var _morphInFlight = 0;
  var _lifecycleGeneration = 0;
  var _closeLayoutHold = null;

  // PC の panel は listing の高さに参加する。閉じるアニメ中だけ高さを保持し、
  // grid-template-columns の退場完了後に参加を解除する。
  function releasePanelHeightHold() {
    var hold = _closeLayoutHold;
    if (!hold) return;
    _closeLayoutHold = null;
    if (hold.timer !== null) clearTimeout(hold.timer);
    hold.listing.removeEventListener('transitionend', hold.onTransitionEnd);
    hold.listing.classList.remove('ig-filter-closing');
  }

  function holdPanelHeightWhileClosing(listing) {
    releasePanelHeightHold();
    if (!listing || !window.matchMedia(MQ).matches) return;

    listing.classList.add('ig-filter-closing');
    var hold = {
      listing: listing,
      timer: null,
      onTransitionEnd: null,
    };
    var finish = function () {
      if (_closeLayoutHold !== hold) return;
      releasePanelHeightHold();
    };
    hold.onTransitionEnd = function (event) {
      if (event.target === listing && event.propertyName === 'grid-template-columns') finish();
    };
    listing.addEventListener('transitionend', hold.onTransitionEnd);
    _closeLayoutHold = hold;

    // transitionend が発火しない環境の保険。reduced-motion 時は次 task で即解放。
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    hold.timer = setTimeout(finish, reduceMotion ? 0 : 450);
  }

  function releaseModalInert() {
    while (_currentInerted.length) {
      var element = _currentInerted.pop();
      try { element.inert = false; } catch (_) {}
    }
  }

  function applyModalInert(panel) {
    releaseModalInert();
    if (!panel || window.matchMedia(MQ).matches) return;

    var element = panel;
    while (element && element.parentElement && element !== document.body) {
      var parent = element.parentElement;
      for (var i = 0; i < parent.children.length; i++) {
        var sibling = parent.children[i];
        if (sibling === element) continue;
        if (sibling.classList && sibling.classList.contains('ig-fp-backdrop')) continue;
        /* §179-7：ヘッダーは inert にしない（§179 ヘッダー直下ルール＝ヘッダーは帰り道として
           残す。全 inert は全画面モーダル時代（§16.24-4 選択肢 A）の設計で、ヘッダーの
           全アイコンが無反応になっていた・user 実機報告 2026-08-13） */
        if (sibling.querySelector && (sibling.classList.contains('header-section') || sibling.querySelector('.header-section'))) continue;
        if (!sibling.inert) {
          sibling.inert = true;
          _currentInerted.push(sibling);
        }
      }
      element = parent;
    }
  }

  function teardown() {
    // listener 以外の DOM 状態も閉状態へ戻す。Theme Editor の unload/load では
    // 旧要素が一時的に DOM に残るため、class/ARIA/backdrop の解除も必須。
    var panel = document.getElementById('ig-filter-panel');
    var listing = document.getElementById('ig-collection-listing');
    var button = document.getElementById('ig-filter-btn');
    var backdrop = document.getElementById('ig-fp-backdrop');
    _lifecycleGeneration += 1;
    _morphInFlight = 0;
    if (_morphRestoreFrame !== null) {
      cancelAnimationFrame(_morphRestoreFrame);
      _morphRestoreFrame = null;
    }
    if (_morphTransitionResumeFrame !== null) {
      cancelAnimationFrame(_morphTransitionResumeFrame);
      _morphTransitionResumeFrame = null;
    }
    document.documentElement.classList.remove('ig-filter-morphing');
    if (_focusTimer) { clearTimeout(_focusTimer); _focusTimer = null; }
    releasePanelHeightHold();
    releaseModalInert();
    if (panel) {
      panel.classList.remove('open');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      panel.removeAttribute('aria-labelledby');
      if (button && button.getAttribute('aria-label')) {
        panel.setAttribute('aria-label', button.getAttribute('aria-label'));
      }
    }
    if (listing) listing.classList.remove('open');
    if (button) button.setAttribute('aria-expanded', 'false');
    if (backdrop) backdrop.classList.remove('open');
    if (_prevBodyOverflow !== null) {
      document.body.style.overflow = _prevBodyOverflow;
      _prevBodyOverflow = null;
    }
    if (_menuXUnregister) { try { _menuXUnregister(); } catch (_) {} _menuXUnregister = null; }
    if (_abortCtrl) { try { _abortCtrl.abort(); } catch (_) {} _abortCtrl = null; }
    window.__igFilterOpen = false;
  }

  function init() {
    // Phase H-5 v6 H2（Codex 再監査）：先に teardown。新 panel が取れない
    // 経路（section unload・Theme Editor で section 削除等）でも旧 inert/overflow が
    // 残らないよう順序を反転（旧 return 前 teardown だと section 削除で state 漏れ）。
    teardown();
    var panel = document.getElementById('ig-filter-panel');
    var btn = document.getElementById('ig-filter-btn');
    var backdrop = document.getElementById('ig-fp-backdrop');
    var listing = document.getElementById('ig-collection-listing');
    if (!panel || !btn) return; // collection ページ以外では発火しない
    _abortCtrl = new AbortController();
    var sig = { signal: _abortCtrl.signal };

    var fpClose = panel.querySelector('.ig-fp-close');
    var fpShow = panel.querySelector('.ig-fp-show');
    var fpClear = panel.querySelector('.ig-fp-clear');
    var mql = window.matchMedia(MQ);

    // Phase H-5 v5 H1：SP モーダル一貫化＝aria-modal="true" と実際の inert 範囲を
    // 揃えるため、header/nav/toolbar の inert 例外を撤回。全兄弟を inert 化して
    // モーダル定義と一致させる（Codex H1 指摘）。
    // PC は trapOn() を呼ばない設計（setOpen 内 isSP のみ）ので影響なし。
    function trapOn() {
      applyModalInert(panel);
    }
    function trapOff() {
      releaseModalInert();
    }

    // ── 開閉状態
    function isOpen() {
      return panel.classList.contains('open');
    }

    function setDialogSemantics(isSPandOpen) {
      if (isSPandOpen) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        panel.setAttribute('aria-labelledby', 'ig-fp-title');
        panel.removeAttribute('aria-label');
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-modal');
        panel.removeAttribute('aria-labelledby');
        panel.setAttribute('aria-label', btn.getAttribute('aria-label') || 'Filter');
      }
    }

    // Phase H-5 v5 C3：open 時 focus timer を module-level に保持し teardown/close で解除。
    // 60ms 遅延中に close された時に非表示 panel 内へ focus が戻る競合を防ぐ。
    function moveFocusIntoPanel(isSP) {
      if (!isSP) return; // PC は non-modal・自動 focus 移動は避ける
      if (_focusTimer) { clearTimeout(_focusTimer); _focusTimer = null; }
      _focusTimer = setTimeout(function () {
        _focusTimer = null;
        // 発火時に条件を再確認：SP のまま・panel が open・panel が DOM 内
        if (!isOpen() || mql.matches || !panel.isConnected) return;
        /* §179-7：最初の部品（BADGE の見出し等）へ送ると開扉のたびに枠線が出る
           （user 実機報告 2026-08-13）→ 焦点は器（role=dialog の panel）そのものへ */
        if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
        var target = panel;
        if (typeof target.focus === 'function') {
          try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); }
        }
      }, 60);
    }

    // Phase H-5 v5 H3：close 理由による分岐。
    //   reason: 'user'（ESC/close btn/backdrop）→ returnFocus + scroll lock 解除
    //   reason: 'exclusive'（他 overlay が開いた・排他） → focus は次 overlay へ hand-off
    //   reason: 'breakpoint'（PC↔SP 切替）→ cleanup のみ、focus は動かさない
    function setOpen(open, reason) {
      var isSP = !mql.matches;
      reason = reason || 'user';

      if (open) {
        // 閉じる途中の再 open は、高さ解放予約を取り消す。
        releasePanelHeightHold();
        window.__igFilterOpen = true;
        panel.classList.add('open');
        if (listing) listing.classList.add('open');
        setDialogSemantics(isSP);
        if (isSP) {
          if (backdrop) backdrop.classList.add('open');
          /* §179：body ロックは廃止（ヘッダーを残す面＝§16.24-3 の非ロック文法。
             sticky ヘッダーを殺さない。背景は inert＋パネルの overscroll contain で動かさない） */
          trapOn();
        }
        btn.setAttribute('aria-expanded', 'true');
        window.__menuX.announce(F_ID);
        try {
          document.querySelectorAll('theme-drawer[open]').forEach(function (d) {
            if (d && typeof d.close === 'function') d.close();
          });
        } catch (_) {}
        moveFocusIntoPanel(isSP);
      } else {
        window.__igFilterOpen = false;
        // PC は退場中のみ panel 高を保持。SP/breakpoint 切替は通常フロー外なので即解放。
        if (!isSP && reason !== 'breakpoint') {
          holdPanelHeightWhileClosing(listing);
        } else {
          releasePanelHeightHold();
        }
        // Phase H-5 v5 C3：pending focus timer を必ず解除
        if (_focusTimer) { clearTimeout(_focusTimer); _focusTimer = null; }
        panel.classList.remove('open');
        if (listing) listing.classList.remove('open');
        setDialogSemantics(false);
        if (backdrop) backdrop.classList.remove('open');
        trapOff();
        // Phase H-5 v6 H3 修正（Codex 再監査 FAIL）：
        // exclusive でも「filter 自身が付けた body.overflow は自分で解放する」。
        // 次 overlay の scroll lock は次 overlay 自身が管理する（Horizon の theme-drawer は
        // html[scroll-lock] を使い body.overflow は触らない・theme-drawer.js:218）。
        // 従って exclusive でも filter 由来 lock を残すとページが scroll 不能で残る。
        document.body.style.overflow = _prevBodyOverflow !== null ? _prevBodyOverflow : '';
        _prevBodyOverflow = null;
        btn.setAttribute('aria-expanded', 'false');
        // focus 復帰は user 起因の close のみ
        // （排他は次 overlay が focus を持つ・breakpoint は静かに終わる）
        if (reason === 'user') {
          try { btn.focus({ preventScroll: true }); } catch (_) { btn.focus(); }
        }
      }
    }

    // ── 排他 register：他 overlay が menu:open を announce したら閉じる
    // Phase H-5 v5 H2：unregister 関数を保持して teardown で解除
    _menuXUnregister = window.__menuX.register(F_ID, function () {
      if (isOpen()) setOpen(false, 'exclusive');
    });

    // C-5：Horizon 純正の theme-drawer:open を bridge（cart drawer / SP header drawer /
    //      search drawer 等が全て theme-drawer 派生・event 名は theme-drawer.js:407 で確認）
    // Phase H-5 v5 H3：排他 close＝focus/scroll lock は次 drawer 側で管理
    document.addEventListener('theme-drawer:open', function () {
      if (isOpen()) setOpen(false, 'exclusive');
    }, sig);

    // ── click ハンドラ（全て user 起因＝returnFocus + scroll lock 解除）
    btn.addEventListener('click', function () { setOpen(!isOpen(), 'user'); }, sig);
    if (fpClose) fpClose.addEventListener('click', function () { setOpen(false, 'user'); }, sig);
    if (fpShow) fpShow.addEventListener('click', function () { setOpen(false, 'user'); }, sig);
    if (backdrop) backdrop.addEventListener('click', function () { setOpen(false, 'user'); }, sig);

    // F-2：SP 「すべて解除」を外部 .ig-chips 内の facets__clear-all-link へ委譲
    // Phase E で chips を panel 外に移した副作用。panel.querySelector では見つからないため
    // document.querySelector で探す。上の bindExternalChipsToForm で bind 済のリンクを click。
    if (fpClear) {
      fpClear.addEventListener('click', function () {
        var clearAll = document.querySelector(
          '.ig-chips .facets__clear-all-link, .ig-chips [href*="clear=1"], .ig-chips a[data-clear-all]'
        );
        if (clearAll && typeof clearAll.click === 'function') {
          clearAll.click();
        }
      }, sig);
    }

    // ── ESC で閉じる（user 起因・focus 復帰あり）
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) setOpen(false, 'user');
    }, sig);

    // ── breakpoint 切替時：静かに閉じる（focus 移動なし）
    mql.addEventListener('change', function () {
      if (isOpen()) setOpen(false, 'breakpoint');
    }, sig);

    // ── pagehide で body scroll 解除（BFCache 対策）
    window.addEventListener('pagehide', function () {
      releasePanelHeightHold();
      document.body.style.overflow = _prevBodyOverflow !== null ? _prevBodyOverflow : '';
      _prevBodyOverflow = null;
      trapOff();
    }, sig);

    // ── popstate 補完（Codex R5 対策・無条件 reload）
    window.addEventListener('popstate', function () {
      window.location.reload();
    }, sig);

    // ── D-5：B2 件数併記
    // Liquid が出力した ig-facet-counts JSON を読み、各 label に (n) span を注入する。
    // 純正 list-filter は value.count を DOM 出力しないため（Codex 指摘）。
    injectFacetCounts(panel);

    // ── E-1 改（§179-9・user 裁定 2026-08-13）：初期は全軸 closed。
    //    旧「先頭 2 軸 open」（§16.23）は撤回＝6 軸が目次として一望でき、検索シートと同じ骨格
    var allDetails = panel.querySelectorAll('.ig-filter-panel__body details.facets__panel');
    allDetails.forEach(function (d) {
      d.removeAttribute('open');
    });

    // ── F-1：外部 chips の form バインド（form 外の facet-remove-component が動くように）
    bindExternalChipsToForm();

    // ── Phase H：clear-all の text を「すべてクリア」→「すべて解除」
    relabelClearAll();

  }

  function injectFacetCounts(panel) {
    var scripts = panel.querySelectorAll('script.ig-facet-counts');
    if (!scripts.length) return;
    scripts.forEach(function (s) {
      var data;
      try { data = JSON.parse(s.textContent); } catch (_) { return; }
      if (!Array.isArray(data)) return;
      data.forEach(function (item) {
        // §260：在庫の軸には件数を出さない（Shopify の在庫の件数は当てにならない＝§255）。
        // ここで足すと、スイッチから消した件数が絞り込みの差し替えのたびに「(0)」で戻る。
        if (item.pn === 'filter.v.availability') return;
        // CSS.escape で属性値を安全にエスケープ
        var esc = (typeof CSS !== 'undefined' && CSS.escape) ? CSS.escape : function (x) { return x; };
        var cb = panel.querySelector(
          'input[type="checkbox"][name="' + esc(item.pn) + '"][value="' + esc(item.v) + '"]'
        );
        if (!cb) return;
        // label のテキスト要素を探す（.checkbox__label-text / .facets__pill-label / .facets__image-label / label 直下）
        var container = cb.closest('label') || cb.parentElement;
        if (!container) return;
        var target = container.querySelector('.checkbox__label-text')
                  || container.querySelector('.facets__pill-label')
                  || container.querySelector('.facets__image-label');
        if (!target) {
          // 汎用フォールバック：label の最後のテキストノードの直後に挿入
          target = container;
        }
        // 既に注入済みならスキップ（Phase D-5 の JS 注入分）
        if (target.querySelector('.ig-facet-count')) return;
        // Phase H：新 igniere-list-filter snippet が Liquid 側で .fgrp__vc を出力済みの label はスキップ
        if (container.querySelector('.fgrp__vc')) return;
        var count = document.createElement('span');
        count.className = 'ig-facet-count';
        count.setAttribute('aria-hidden', 'true');
        count.textContent = '(' + item.c + ')';
        target.appendChild(count);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Section Rendering API の morph 後の再 init（idempotent 済）
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#ig-filter-panel')) {
      init();
      observeCurrentListing();
    }
  });
  // Phase H-5 v6 H2（Codex 再監査）：Theme Editor で section 削除された時の完全 cleanup
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector && (e.target.querySelector('#ig-filter-panel') ||
        e.target.id === 'ig-filter-panel')) {
      if (mo) mo.disconnect();
      teardown();
    }
  });

  // D-5：facets.js による morph 後の (n) 再注入
  // shopify:section:load は section reload 時のみ発火・facets.js の filter morph では発火しない。
  // 代わりに Shopify Spring '26 標準イベント shopify:collection:update / shopify:search:update を listen。
  function reinjectAfterMorph() {
    var panel = document.getElementById('ig-filter-panel');
    var listing = document.getElementById('ig-collection-listing');
    var btn = document.getElementById('ig-filter-btn');
    var backdrop = document.getElementById('ig-fp-backdrop');
    if (panel) {
      injectFacetCounts(panel);
      // Phase H-5 v6 C2：morph 後の atomic restoreOpenState。
      // class + aria-expanded + SP なら role/aria-modal/backdrop/scroll lock/inert
      // まで atomic に復元（Codex C2 指摘：inert 復元漏れが H1 と一緒に FAIL）。
      var wasOpen = (window.__igFilterOpen === true) || (btn && btn.getAttribute('aria-expanded') === 'true');
      if (wasOpen) {
        panel.classList.add('open');
        if (listing) listing.classList.add('open');
        if (btn) btn.setAttribute('aria-expanded', 'true');
        var isSP = !window.matchMedia(MQ).matches;
        if (isSP) {
          panel.setAttribute('role', 'dialog');
          panel.setAttribute('aria-modal', 'true');
          panel.setAttribute('aria-labelledby', 'ig-fp-title');
          panel.removeAttribute('aria-label');
          if (backdrop) backdrop.classList.add('open');
          /* §179：body ロック廃止（上の setOpen と同じ理由） */
          applyModalInert(panel);
        }
      }
    }
    bindExternalChipsToForm();
    relabelClearAll();
  }
  // 標準イベントの e.promise が settle した次フレームで復元する。
  // 各 morph の完了を拾い、同一フレーム分だけを合成することで、後発 morph の
  // 取りこぼしと MutationObserver による完了前の二重復元を防ぐ。
  function scheduleMorphRestore() {
    if (_morphRestoreFrame !== null) return;
    var generation = _lifecycleGeneration;
    _morphRestoreFrame = requestAnimationFrame(function () {
      _morphRestoreFrame = null;
      if (generation !== _lifecycleGeneration) return;
      reinjectAfterMorph();
      resumeMorphTransitions();
    });
  }

  function suppressMorphTransitions() {
    if (window.__igFilterOpen !== true) return;
    if (_morphTransitionResumeFrame !== null) {
      cancelAnimationFrame(_morphTransitionResumeFrame);
      _morphTransitionResumeFrame = null;
    }
    document.documentElement.classList.add('ig-filter-morphing');
  }

  function resumeMorphTransitions() {
    if (_morphInFlight > 0) return;
    if (_morphTransitionResumeFrame !== null) cancelAnimationFrame(_morphTransitionResumeFrame);
    var generation = _lifecycleGeneration;
    // open 復元のスタイルが確定した後に transition を戻す。
    _morphTransitionResumeFrame = requestAnimationFrame(function () {
      _morphTransitionResumeFrame = requestAnimationFrame(function () {
        _morphTransitionResumeFrame = null;
        if (generation !== _lifecycleGeneration || _morphInFlight > 0) return;
        document.documentElement.classList.remove('ig-filter-morphing');
      });
    });
  }

  // IGNIERE §163-25：0 件の文言切替を morph 後も効かせる。
  // section 内の inline script は Section Rendering API の morph で再実行されない
  // （morph.js:539 が再実行するのは .shopify-app-block の script[src] のみ）。
  // 初回描画は inline script が担い（描画前に走るので文言の一瞬のちらつきが出ない）、
  // morph 経由の再描画はこの常駐関数が担う。0 件では絞り込みパネル自体が出ないため、
  // panel/listing の存在に依存させない。
  function applyNoneVariant() {
    var box = document.querySelector('[data-srp-none]');
    if (!box) return;
    var qs;
    try {
      qs = new URLSearchParams(location.search);
    } catch (err) {
      return;
    }
    var hasFilter = false;
    qs.forEach(function (v, k) {
      if (k.indexOf('filter.') === 0) hasFilter = true;
    });
    var hasWord = (qs.get('q') || '').trim() !== '';
    var pick = hasFilter ? (hasWord ? 'both' : 'filter') : 'word';
    box.querySelectorAll('[data-none]').forEach(function (el) {
      el.toggleAttribute('hidden', el.getAttribute('data-none') !== pick);
    });
    var act = box.querySelector('[data-none-act]');
    if (act) act.toggleAttribute('hidden', !hasFilter);
    var wh = box.querySelector('[data-none-word]');
    if (wh) wh.toggleAttribute('hidden', hasFilter || !hasWord);
    var wf = box.querySelector('[data-none-form]');  // §280 D25：言葉がある時だけ探し直す欄を出す
    if (wf) wf.toggleAttribute('hidden', !hasWord);
    // §280「今の絞り込み」を並べる（assets/igniere-search-none.js）。結果のある面から絞り込みで 0 件に入った時は
    // 差し替え（morph）で入った script タグが実行されないので、まだ読んでいなければここで読む（読めば自分で並べる）。
    if (window.igNoneChips) {
      window.igNoneChips();
    } else if (!window.__igNoneLoading) {
      var tag = document.querySelector('script[src*="igniere-search-none"]');
      if (tag) {
        window.__igNoneLoading = true;
        var sc = document.createElement('script');
        sc.src = tag.src;
        document.head.appendChild(sc);
      }
    }
  }

  // §336（AUD-20261016-001・2026-10-17・代表「一覧の先頭へ戻す・PC とスマホの両方」）：
  // 絞り込み・並び替え・チップの解除で結果が短くなると、ページが縮んで送りの位置が新しい下端に
  // 切り詰められ、フッターへ飛んだように見える（Horizon の facets.js は送りを扱わない）。
  // 一覧の先頭（器の上端＝ツールバーの元の位置）が貼り付いたツールバーの下に隠れている時だけ、
  // 差し替えの前（このイベントは描き直しの fetch より先に同期で届く）に、ツールバーが
  // 貼り付く位置ちょうどまで戻す。パネルは貼り付いていた時と同じ高さに来るので、押したチェックは動かない。
  // 先頭が見えている時は動かさない。送りを担うのは PC（990 以上）は .page-wrapper・スマホは文書（HANDOFF §5-5）。
  function returnToResultsHead(e) {
    var form = e && e.target && e.target.closest ? e.target.closest('facets-form-component') : null;
    var listing = document.getElementById('ig-collection-listing');
    if (!form || !listing) return;
    var scroller = window.matchMedia(MQ).matches
      ? document.querySelector('.page-wrapper')
      : (document.scrollingElement || document.documentElement);
    if (!scroller) return;
    var toolbar = listing.querySelector('.ig-toolbar');
    var stickTop = toolbar ? parseFloat(getComputedStyle(toolbar).top) || 0 : 0;
    var gap = listing.getBoundingClientRect().top - stickTop;
    if (gap >= -1) return;
    scroller.scrollTo({ top: Math.max(0, scroller.scrollTop + gap), behavior: 'instant' });
  }

  function handleMorphEvent(e) {
    returnToResultsHead(e);
    suppressMorphTransitions();
    applyNoneVariant();
    var promise = e && e.promise;
    if (!promise || typeof promise.then !== 'function') {
      scheduleMorphRestore();
      return;
    }

    var generation = _lifecycleGeneration;
    _morphInFlight += 1;
    function settled() {
      if (generation !== _lifecycleGeneration) return;
      _morphInFlight = Math.max(0, _morphInFlight - 1);
      scheduleMorphRestore();
      applyNoneVariant();
    }
    Promise.resolve(promise).then(settled, settled);
  }

  // F-1：外部 .ig-chips の解除を document delegation で捕捉。
  // facets.js:570 removeFilter は最寄り facets-form-component を探す設計だが .ig-chips は
  // form 外なので closest で見つからず解除が動かない。document click を捕捉して直接
  // form.updateFiltersByURL を呼ぶ。動的生成される chips にも自動追従。
  // idempotent：一度だけ document に bind すればよい。
  function bindExternalChipsToForm() {
    if (window.__igChipsBound) return;
    window.__igChipsBound = true;

    function getForm() {
      return document.querySelector('#ig-filter-panel facets-form-component');
    }

    function handleRemove(e) {
      // 個別 chip
      var chip = e.target.closest('.ig-chips facet-remove-component[data-url]');
      if (chip) {
        if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
        var form = getForm();
        if (!form || typeof form.updateFiltersByURL !== 'function') return;
        e.preventDefault();
        e.stopPropagation();
        form.updateFiltersByURL(chip.dataset.url);
        return;
      }
      // 「すべて解除」
      var clearAll = e.target.closest('.ig-chips .facets__clear-all-link');
      if (clearAll) {
        var form2 = getForm();
        if (!form2 || typeof form2.updateFiltersByURL !== 'function') return;
        var url = clearAll.dataset.url
              || clearAll.getAttribute('href')
              || (clearAll.closest('[data-url]') && clearAll.closest('[data-url]').dataset.url);
        if (!url) return;
        e.preventDefault();
        form2.updateFiltersByURL(url);
      }
    }

    document.addEventListener('click', handleRemove, true);
    document.addEventListener('keydown', handleRemove, true);
  }
  document.addEventListener('shopify:collection:update', handleMorphEvent);
  document.addEventListener('shopify:search:update', handleMorphEvent);

  // Phase H：clear-all の text を「すべてクリア」→「すべて解除」に差し替え（user 実機指摘 2026-07-20）。
  // Horizon 翻訳を触らずに JS で textContent を直接置換する。
  function relabelClearAll() {
    document.querySelectorAll('.ig-chips .facets__clear-all-link, .ig-filter-panel .facets__clear-all-link').forEach(function (b) {
      var t = (b.textContent || '').trim();
      if (t === 'すべてクリア' || t.toLowerCase() === 'clear all') {
        b.textContent = 'すべて解除';
      }
    });
  }

  // Phase H：MutationObserver で morph 後の open 状態復元 + (n) 再注入。
  // shopify:collection:update イベントの発火経路（@shopify/events）は Horizon fork の
  // カスタム要素上で発火し、document まで bubble しない場合があるため、DOM 変化そのものを
  // 追う MutationObserver で確実に補完する。
  // 対象は #ig-collection-listing（class 属性変化 + subtree childList）。
  var mo = new MutationObserver(function () {
    // §163-25：0 件では panel が無いので、guard より前に文言切替を回す。
    applyNoneVariant();
    var listing = document.getElementById('ig-collection-listing');
    var panel = document.getElementById('ig-filter-panel');
    if (!listing || !panel) return;

    // class の乖離は promise 完了を待たず microtask で戻し、1 frame の閉状態描画を防ぐ。
    // 件数・ARIA・SP inert の完全復元は引き続き promise 完了後に行う。
    if (
      window.__igFilterOpen === true &&
      (!listing.classList.contains('open') || !panel.classList.contains('open'))
    ) {
      suppressMorphTransitions();
      listing.classList.add('open');
      panel.classList.add('open');
      var button = document.getElementById('ig-filter-btn');
      if (button) button.setAttribute('aria-expanded', 'true');
      if (_morphInFlight === 0) scheduleMorphRestore();
    }

    // (n) 再注入（既存の Phase D-5 相当・新 fgrp__vc がある場合は injectFacetCounts 内で skip）
    if (!panel.querySelector('.ig-facet-count') && !panel.querySelector('.fgrp__vc')) {
      injectFacetCounts(panel);
    }
    relabelClearAll();
  });
  function observeCurrentListing() {
    mo.disconnect();
    var listing = document.getElementById('ig-collection-listing');
    if (listing) {
      mo.observe(listing, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCurrentListing, { once: true });
  } else {
    observeCurrentListing();
  }
})();

/* ============================================================
   §171 chips の 1 行固定＋「＋N」（2026-08-13 user 裁定）
   入る分だけ出し、あふれた分は「＋N」に畳む＝検索パネル §162-33 と同じ実測幅方式。
   「すべて解除」と「＋N」の席を先に確保してから pill を詰める。
   morph（絞り込みの差し替え）で chips が描き直されても、listing の
   MutationObserver が拾って畳み直す（自分の畳み込みによる変異は除外）。
   「＋N」を押すと絞り込みパネルが開く（全量が見える唯一の場所へ渡す）。
   ============================================================ */
(function () {
  function fitChips() {
    document.querySelectorAll('#MainContent .ig-chips').forEach(function (box) {
      var more = box.querySelector('.ig-chips__more');
      if (!more) return;
      var pills = Array.prototype.slice.call(box.querySelectorAll('facet-remove-component[role="button"]'));
      var clearPill = box.querySelector('facet-remove-component:not([role="button"])');
      pills.forEach(function (p) { p.hidden = false; });
      if (!pills.length) { more.hidden = true; return; }

      more.hidden = false;
      more.textContent = '＋' + pills.length;
      var boxStyle = getComputedStyle(box);
      // clientWidth は内側余白を含む＝SP（padding 0 12px）で 24px 過大に見積もり
      // 「すべて解除」が右へ押し出されて見切れた（実測 2026-08-13）。中身の幅で測る
      var avail = box.clientWidth
        - (parseFloat(boxStyle.paddingLeft) || 0)
        - (parseFloat(boxStyle.paddingRight) || 0);
      if (avail <= 0) { more.hidden = true; return; }
      var gap = parseFloat(boxStyle.columnGap) || 12;
      var moreW = more.getBoundingClientRect().width;
      var reserve = clearPill ? clearPill.getBoundingClientRect().width + gap : 0;

      var used = 0;
      var shown = 0;
      for (var i = 0; i < pills.length; i++) {
        var w = pills[i].getBoundingClientRect().width;
        var next = used + (shown ? gap : 0) + w;
        var isLast = i === pills.length - 1;
        var need = next + (isLast ? 0 : gap + moreW) + reserve;
        if (need <= avail) { used = next; shown += 1; } else break;
      }
      pills.forEach(function (p, i) { p.hidden = i >= shown; });
      var rest = pills.length - shown;
      if (rest > 0) {
        more.hidden = false;
        more.textContent = '＋' + rest;
      } else {
        more.hidden = true;
      }
    });
  }

  var rafId = 0;
  function queueFit() {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(fitChips);
  }

  window.addEventListener('resize', queueFit);

  var mo = new MutationObserver(function (mutations) {
    var relevant = mutations.some(function (m) {
      return !(m.target instanceof Element && m.target.closest('.ig-chips__more'));
    });
    if (relevant) queueFit();
  });
  function observeChips() {
    var listing = document.getElementById('ig-collection-listing');
    if (listing) mo.observe(listing, { childList: true, subtree: true });
    queueFit();
  }

  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    if (!e.target.closest('.ig-chips__more')) return;
    var btn = document.getElementById('ig-filter-btn');
    if (btn && btn.getAttribute('aria-expanded') !== 'true') btn.click();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeChips, { once: true });
  } else {
    observeChips();
  }
})();

/* ============================================================
   §175 カテゴリー軸の第一階層グループ表示＋見出しで一括選択（2026-08-13 user 裁定）
   全商品一覧・検索結果では第二階層が直列に並び読みにくい（実測 9 本）＝
   第一階層の見出しでグループ化し、見出しを押すと配下をまとめて選択できるようにする
   （＝第一階層フィルターと同義になる・データは増やさない）。
   現れている値が 1 グループに収まる棚の中では組まない（素のまま＝ノイズを足さない）。
   morph（絞り込しの差し替え）で描き直されても listing の監視で組み直す。
   グループ表（大分類→細分類）は直書きしない（2026-10-03）。正本は基幹の細分類表＋大分類表で、
   生成器（作業/tools/build_category_groups.py）が snippets/igniere-category-groups.liquid の
   <script class="ig-category-groups"> へ書き出す。ここはそれを読むだけ（無ければグループ化しない）。

   2026-10-03（代表の裁定＝CATEGORY と ITEM TYPE の統合）：S&D に大分類の軸がある店では、一覧の絞り込みは
   Liquid（snippets/igniere-list-filter-tree.liquid）が大分類の行（本物の箱）→ 細分類の行の 1 本の木
   「CATEGORY」を描く（目印＝details[data-ig-tree]）。木では下の groupize（JS で見出しを組む旧の経路）と
   見出しの箱の一括選択は働かせない。旧の経路は「大分類の軸が無い店」の保険として残す。
   2026-10-04（§259）：木の親の箱は「配下の子を全部選ぶ／外す」の入口（name なし・URL に入るのは子だけ）。
   木の JS＝下の syncTree（親の見た目を子から決める）と、親の箱の click で配下の子をそろえる処理。
   ============================================================ */
(function () {
  var GROUPS = null;
  function categoryGroups() {
    if (GROUPS) return GROUPS;
    var el = document.querySelector('script.ig-category-groups');
    if (!el) return [];  /* まだ無い時は覚えない（後で読み直せるように） */
    var list = [];
    try {
      var parsed = JSON.parse(el.textContent);
      if (Array.isArray(parsed)) list = parsed;
    } catch (_) { list = []; }
    GROUPS = list;
    return list;
  }
  var PARAM = 'filter.p.product_type';

  function container() {
    var input = document.querySelector('.ig-filter-panel input[name="' + PARAM + '"]');
    if (!input) return null;
    return input.closest('details') || input.closest('fieldset');
  }

  function rowOf(input) {
    return input.closest('label') || input.closest('li') || input.parentElement;
  }

  function heads(box) {
    return Array.prototype.slice.call(box.querySelectorAll('.ig-fpg__head'));
  }

  /* 見出しの箱＝本物のチェックボックス（2026-10-03）。値の箱と同じ部品・同じ class（fgrp__input）
     なので、どのブラウザでも同じ見た目になる。全選択＝checked／一部＝indeterminate（横棒）／なし＝空。
     name を持たない＝絞り込みの URL（FormData）には入らない。 */
  function syncHeads(box) {
    heads(box).forEach(function (head) {
      var inputs = head.parentElement.querySelectorAll('input[name="' + PARAM + '"]');
      var checked = Array.prototype.filter.call(inputs, function (i) { return i.checked; }).length;
      var state = checked === 0 ? 'none' : (checked === inputs.length ? 'all' : 'some');
      head.dataset.state = state;
      var cb = head.querySelector('.ig-fpg__cb');
      if (cb) {
        cb.checked = state === 'all';
        cb.indeterminate = state === 'some';
      }
    });
  }

  /* 木（Liquid が描いた 1 本の木）か。木では旧の groupize・見出しの一括選択を働かせない。 */
  function isTree(el) {
    return !!(el && el.closest && el.closest('[data-ig-tree]'));
  }

  /* 木（§259・§260・2026-10-04）：親の箱は「配下の子を全部選ぶ／全部外す」の入口。name を持たない見た目だけの
     箱で、URL に入るのは子（商品タイプ）だけ。親の見た目は子から決める（全部＝✓／一部＝横棒／無し＝空）。
     大分類の軸（場所）は Liquid が hidden の input で持ち続ける＝ここでは触らない。 */
  function syncTree() {
    var wraps = document.querySelectorAll('.ig-filter-panel details[data-ig-tree] .ig-fpg');
    Array.prototype.forEach.call(wraps, function (wrap) {
      var cb = wrap.querySelector('.ig-fpg__pcb');
      if (!cb) return;
      var kids = wrap.querySelectorAll('.ig-fpg__items input[type="checkbox"]');
      var on = Array.prototype.filter.call(kids, function (i) { return i.checked; }).length;
      cb.checked = kids.length > 0 && on === kids.length;
      cb.indeterminate = on > 0 && on < kids.length;
    });
  }
  /* 親の箱の click（change より先に来る）で配下の子をそろえる。絞り込みそのものは、この後の change を
     facet-inputs-component（facets.js）が拾って FormData から URL を組む。 */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof HTMLInputElement) || !t.classList.contains('ig-fpg__pcb')) return;
    var wrap = t.closest('.ig-filter-panel details[data-ig-tree] .ig-fpg');
    if (!wrap) return;
    var want = t.checked;
    Array.prototype.forEach.call(wrap.querySelectorAll('.ig-fpg__items input[type="checkbox"]'), function (i) {
      if (!i.disabled) i.checked = want;
    });
    syncTree();
  }, true);
  document.addEventListener('change', function (e) {
    if (e.target instanceof Element && e.target.closest('.ig-filter-panel details[data-ig-tree]')) syncTree();
  });

  function groupize() {
    var box = container();
    if (!box || isTree(box) || box.querySelector('.ig-fpg')) return;
    var inputs = Array.prototype.slice.call(box.querySelectorAll('input[name="' + PARAM + '"]'));
    if (!inputs.length) return;
    var byValue = {};
    inputs.forEach(function (inp) { byValue[inp.value] = inp; });

    var groups = categoryGroups();
    if (!groups.length) return;
    /* 表の形（2026-10-03）＝[{code, name, handle, types:[細分類名…]}…]（優先順位の順） */
    var present = [];
    groups.forEach(function (g) {
      if (!g || !Array.isArray(g.types)) return;
      var vals = g.types.filter(function (v) { return byValue[v]; });
      if (vals.length) present.push([g.name || g.code || '', vals]);
    });
    var known = [];
    present.forEach(function (g) { known = known.concat(g[1]); });
    var rest = inputs.filter(function (inp) { return known.indexOf(inp.value) < 0; }).map(function (i) { return i.value; });
    if (rest.length) present.push(['その他', rest]);
    /* 1 グループに収まる時は組まない（棚の中の文脈） */
    if (present.length < 2) return;

    var host = rowOf(inputs[0]).parentElement;
    present.forEach(function (g) {
      var wrap = document.createElement('div');
      wrap.className = 'ig-fpg';
      var head = document.createElement('label');
      head.className = 'ig-fpg__head';
      head.innerHTML = '<input type="checkbox" class="fgrp__input ig-fpg__cb"><span class="ig-fpg__name"></span>';
      head.lastChild.textContent = g[0];
      var items = document.createElement('div');
      items.className = 'ig-fpg__items';
      g[1].forEach(function (v) { items.appendChild(rowOf(byValue[v])); });
      wrap.appendChild(head);
      wrap.appendChild(items);
      host.appendChild(wrap);
    });
    syncHeads(box);
  }

  /* 見出しの箱の change は絞り込みへ流さない（配下の値の change 1 回だけで絞り込む）。
     捕捉段階で止める＝facet-inputs-component の on:change まで届かない。 */
  document.addEventListener('change', function (e) {
    if (e.target instanceof Element && e.target.classList.contains('ig-fpg__cb') && !isTree(e.target)) e.stopPropagation();
  }, true);

  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    /* label を押すと label と箱の 2 回 click が来る＝箱の 1 回だけ拾う */
    if (!e.target.classList.contains('ig-fpg__cb') || isTree(e.target)) return;
    var head = e.target.closest('.ig-fpg__head');
    if (!head) return;
    var inputs = head.parentElement.querySelectorAll('input[name="' + PARAM + '"]');
    var allOn = Array.prototype.every.call(inputs, function (i) { return i.checked; });
    var last = null;
    Array.prototype.forEach.call(inputs, function (i) { i.checked = !allOn; last = i; });
    var box = container();
    if (box) syncHeads(box);
    if (last) last.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('change', function (e) {
    if (!(e.target instanceof Element)) return;
    var nm = e.target.getAttribute('name');
    if (nm !== PARAM) return;
    var box = container();
    if (box && !isTree(box)) syncHeads(box);
  });

  /* ============ §179-8 検索シートとの機能部品統一（2026-08-13 user 裁定） ============
     ブランド＝ロゴのタイル（検索 §162-7 と同型）／在庫＝トグル 1 本／価格＝下限・上限の空欄式。
     どれも Horizon facets の実 DOM を**動かさず装いだけ替える**（input は
     facet-inputs-component の中に残す＝on:change の配線を壊さない）。
     morph 後の再適用は groupize と同じ観測経路（queueGroupize の rAF）に相乗り。 */
  function fpanelEl() {
    return document.querySelector('#MainContent .ig-filter-panel');
  }

  function igBrandMap() {
    if (igBrandMap._m) return igBrandMap._m;
    var el = document.querySelector('[data-ig-brand-map]');
    var list = [];
    try { list = JSON.parse(el ? el.textContent : '[]'); } catch (_) { list = []; }
    var m = {};
    list.forEach(function (x) { m[String(x.n).trim()] = x; });
    igBrandMap._m = m;
    return m;
  }

  function tileizeBrand() {
    var p = fpanelEl();
    if (!p) return;
    var anyInput = p.querySelector('input[name="filter.p.vendor"]');
    if (!anyInput) return;
    var fieldset = anyInput.closest('fieldset');
    if (!fieldset || fieldset.querySelector('.ig-sp__tiles')) return;
    var labels = Array.prototype.slice.call(fieldset.querySelectorAll('label'));
    if (!labels.length || labels.length > 12) return; // 検索側 §162-11 と同じ上限
    var m = igBrandMap();
    var infos = labels.map(function (lb) {
      var inp = lb.querySelector('input');
      var txt = lb.querySelector('.fgrp__label-text');
      var key = String((txt ? txt.textContent : inp.value)).trim();
      return m[key] || m[String(inp.value).trim()] || null;
    });
    if (infos.some(function (x) { return !x; })) return; // ロゴが 1 つでも欠けたら文字の一覧のまま

    fieldset.classList.add('ig-fpx');
    var grid = document.createElement('div');
    grid.className = 'ig-sp__tiles';
    labels.forEach(function (lb, i) {
      var inp = lb.querySelector('input');
      var txt = lb.querySelector('.fgrp__label-text');
      var vc = lb.querySelector('.fgrp__vc');
      lb.classList.add('ig-sp__tile');
      if (inp.checked) lb.classList.add('is-on');
      var box = document.createElement('span');
      box.className = 'ig-sp__tile-box';
      box.innerHTML = '<span class="ig-sp__tile-logo" style="--ig-logo-src:url(\'' + infos[i].u + '\');--ig-logo-w:' + infos[i].w + '%"></span>';
      if (txt) txt.classList.add('visually-hidden');
      lb.insertBefore(box, txt || null);
      if (vc) { vc.classList.add('ig-sp__tile-cnt'); lb.appendChild(vc); }
      grid.appendChild(lb);
    });
    fieldset.appendChild(grid);
  }

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!(t instanceof Element) || t.getAttribute('name') !== 'filter.p.vendor') return;
    var tile = t.closest('.ig-sp__tile');
    if (tile) tile.classList.toggle('is-on', t.checked);
  });

  function switchizeAvailability() {
    var p = fpanelEl();
    if (!p) return;
    var inp1 = p.querySelector('details.facets__panel input[name="filter.v.availability"][value="1"]');
    if (!inp1) return;
    var det = inp1.closest('details.facets__panel');
    if (!det || det.dataset.igSw === '1') return;
    var inp0 = det.querySelector('input[name="filter.v.availability"][value="0"]');
    if (inp0 && inp0.checked) return; // 「在庫切れ」を選択中は素の形を保つ（解除の道を塞がない）
    var lb1 = inp1.closest('label');
    if (!lb1) return;
    var fieldset = inp1.closest('fieldset');
    if (fieldset) fieldset.classList.add('ig-fpx');
    det.dataset.igSw = '1';
    det.open = true;
    var txt = lb1.querySelector('.fgrp__label-text');
    var vc = lb1.querySelector('.fgrp__vc');
    if (txt) { txt.textContent = '在庫のある商品だけ'; txt.classList.add('ig-sp__sw-lab'); }
    /* §255（2026-10-03）件数は出さない＝Shopify の在庫の軸の件数が当てにならない（一覧では常に 0）。モーダル側も同じ */
    if (vc) vc.remove();
    lb1.classList.add('ig-sp__sw-row');
    inp1.classList.add('ig-sp__sw');
    inp1.setAttribute('role', 'switch');
    inp1.setAttribute('aria-label', '在庫のある商品だけを表示');
    var track = document.createElement('span');
    track.className = 'ig-sp__sw-track';
    track.setAttribute('aria-hidden', 'true');
    track.innerHTML = '<span class="ig-sp__sw-knob"></span>';
    lb1.appendChild(track); // input より後ろ＝ :checked ~ track が効く
    var lb0 = inp0 ? inp0.closest('label') : null;
    if (lb0) lb0.hidden = true;
  }

  function priceizeFilter() {
    var p = fpanelEl();
    if (!p) return;
    var gte = p.querySelector('input[name="filter.v.price.gte"]');
    var lte = p.querySelector('input[name="filter.v.price.lte"]');
    if (!gte || gte.dataset.igPrice === '1') return;
    gte.dataset.igPrice = '1';
    gte.placeholder = '下限';
    if (lte) lte.placeholder = '上限';
    // 注記「最高価格は¥138,000です」→ 検索側の書式「最高 ¥138,000」
    var scope = gte.closest('price-facet-component') || gte.closest('details');
    if (scope) {
      var walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue && node.nodeValue.indexOf('最高価格') >= 0) {
          var mmax = node.nodeValue.match(/¥[\d,]+/);
          if (mmax) node.nodeValue = node.nodeValue.replace(/最高価格は¥[\d,]+です/, '最高 ' + mmax[0]);
          break;
        }
      }
    }
  }

  /* 大分類の軸（custom.major_category）のチップ＝選択中の表示（2026-10-03）。
     チップは Horizon のコア（filter-remove-buttons）が値の生の文字＝記号（we 等）で描くので、
     ここで表示名（ウェア 等）へ書き換える。どの値のチップかは「外した後の URL（data-url）」と
     いま選ばれている値の差で判定する（差がちょうど 1 つの時だけ・「すべて解除」は除く）。 */
  var MAJOR = 'filter.p.m.custom.major_category';
  function relabelMajorChips() {
    var cur = new URLSearchParams(location.search).getAll(MAJOR);
    document.querySelectorAll('#MainContent .ig-filter-panel input[name="' + MAJOR + '"]:checked').forEach(function (i) {
      if (cur.indexOf(i.value) < 0) cur.push(i.value);
    });
    if (!cur.length) return;
    var names = {};
    categoryGroups().forEach(function (g) { if (g && g.code) names[g.code] = g.name; });
    document.querySelectorAll('#MainContent .ig-chips facet-remove-component[data-url]').forEach(function (chip) {
      if (chip.querySelector('.facets__clear-all-link')) return;
      var rest;
      try { rest = new URL(chip.dataset.url, location.origin).searchParams.getAll(MAJOR); } catch (_) { return; }
      var removed = cur.filter(function (v) { return rest.indexOf(v) < 0; });
      if (removed.length !== 1 || !names[removed[0]]) return;
      Array.prototype.forEach.call(chip.childNodes, function (n) {
        if (n.nodeType === 3 && n.textContent.trim() === removed[0]) n.textContent = ' ' + names[removed[0]] + ' ';
      });
    });
  }

  function unifyParts() {
    try { relabelMajorChips(); } catch (_) {}
    try { tileizeBrand(); } catch (_) {}
    try { switchizeAvailability(); } catch (_) {}
    try { priceizeFilter(); } catch (_) {}
  }

  var rafId2 = 0;
  function queueGroupize() {
    cancelAnimationFrame(rafId2);
    rafId2 = requestAnimationFrame(function () {
      groupize();
      syncTree();   // 木の親の横棒（morph 後も同じ経路で合わせる）
      unifyParts(); // §179-8：morph 後もタイル・トグル・価格欄を組み直す
    });
  }
  var mo2 = new MutationObserver(function (mutations) {
    var relevant = mutations.some(function (m) {
      return !(m.target instanceof Element && m.target.closest('.ig-fpg__head'));
    });
    if (relevant) queueGroupize();
  });
  function startGroupize() {
    var listing = document.getElementById('ig-collection-listing');
    if (listing) mo2.observe(listing, { childList: true, subtree: true });
    queueGroupize();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startGroupize, { once: true });
  } else {
    startGroupize();
  }
})();
